import { ExternalLink, Webhook, FileText, ListOrdered } from "lucide-react";

import PageHeader from "../../../../components/PageHeader";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import Modal from "../../../../components/Modal";

import FilaMonitor from "./FilaMonitor";
import { useWebhookCardapioPage } from "../context/WebhookCardapioContext";

const DOC_WEBHOOKS =
  "https://cardapioweb.stoplight.io/docs/api/eedxvdkl0qyhv-webhooks";
const DOC_EVENTOS =
  "https://cardapioweb.stoplight.io/docs/api/ypkccxtuvwv1u-eventos-notificados";

const WebhookCardapioContent = () => {
  const {
    setPage,
    payloadModal,
    closePayloadModal,
    webhookUrl,
    copyToClipboard,
    eventosData,
    isEventosLoading,
    tableData,
    tableActions,
  } = useWebhookCardapioPage();

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 mt-6">
        <PageHeader
          title="Webhook Cardápio Web"
          description="Configure a integração com a API Cardápio Web para receber notificações de eventos (pedidos, alterações, etc.) em tempo real."
        />
      </div>

      <hr className="my-6" />

      <div className="space-y-6">
        <FilaMonitor />

        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <ListOrdered className="text-primary" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Eventos recebidos
            </h3>
          </div>
          {isEventosLoading ? (
            <div className="py-8 text-center text-neutral-500">
              Carregando...
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table
                  headers={["Data/Hora", "Tipo do evento", "Resumo"]}
                  data={tableData}
                  actions={tableActions}
                  actionsColumnTitle="Ver"
                />
              </div>
              {eventosData && eventosData.totalPages > 0 && (
                <Pagination
                  currentPage={eventosData.page}
                  totalPages={eventosData.totalPages}
                  totalItems={eventosData.total}
                  itemsPerPage={eventosData.limit}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Webhook className="text-primary" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              URL para configurar no Cardápio Web
            </h3>
          </div>
          <p className="mb-3 text-sm text-neutral-600">
            No painel do Cardápio Web, acesse{" "}
            <strong>
              CONFIGURAÇÕES &gt; INTEGRAÇÕES &gt; API DE INTEGRAÇÃO &gt;
              Webhooks
            </strong>{" "}
            e cadastre a URL abaixo. O sistema receberá as notificações via HTTP
            POST.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="flex-1 break-all rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 font-mono text-sm text-neutral-800">
              {webhookUrl}
            </code>
            <button
              type="button"
              onClick={copyToClipboard}
              className="rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              Copiar URL
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="text-primary" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Documentação Cardápio Web
            </h3>
          </div>
          <ul className="space-y-3">
            <li>
              <a
                href={DOC_WEBHOOKS}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink size={16} />
                Webhooks – como configurar e usar
              </a>
            </li>
            <li>
              <a
                href={DOC_EVENTOS}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink size={16} />
                Eventos notificados – tipos de evento e payloads
              </a>
            </li>
          </ul>
        </div>
      </div>

      <Modal
        isOpen={!!payloadModal}
        onClose={closePayloadModal}
        title={
          payloadModal
            ? `Evento #${payloadModal.id} – ${payloadModal.eventType ?? "sem tipo"}`
            : "Payload"
        }
      >
        {payloadModal && (
          <pre className="max-h-96 overflow-auto rounded bg-neutral-100 p-4 text-left text-sm">
            {JSON.stringify(payloadModal.payload, null, 2)}
          </pre>
        )}
      </Modal>
    </main>
  );
};

export default WebhookCardapioContent;
